import merge from "deepmerge";
import {StoreService, StoreServiceInstance} from "@/services/storeService";
import msDataProvider from "@/services/msDataProvider";
import moment from "moment";

export class RatingService {

    public rating = {};
    public RATINGS_KEY = 'ratings';

    private version = "";
    private userId = "";
    private currentServerName = "";
    private openBy = "";
    private IS_UPDATED_SESSION_KEY = 'isUpdatedSessionKey'
    private INITIATING_SESSION = 3
    private REPEAT_PERIOD_BY_SESSION = 3
    private REPEAT_PERIOD_BY_DAYS = 5

    constructor(
        private storeService: StoreService
    ) {
        this.getRatingSetting();
    }

    public async init(version, userId) {
        this.version = this.encodeKey(version);
        this.userId = userId;
        await this.getRatingSetting();
        await this.getCurrentServerName();
    }

    public async initRatings(version, userId) {
        const ratings = this.storeService.settingMetaData[this.RATINGS_KEY];
        if (!ratings || !ratings[version] || !ratings[version][userId]) {
            await this.storeService.setMetaData(this.RATINGS_KEY, merge(ratings, {
                [version]: {
                    [userId]: {
                        sessionCount: 1,
                        rating: null,
                        createdTime: new Date().getTime()
                    }
                }
            }))
        }
    }

    public async setRatings(data) {
        if (this.storeService.getMetaData(`${this.RATINGS_KEY}.${this.version}.${this.userId}`)) {
            await this.storeService.setMetaData(`${this.RATINGS_KEY}.${this.version}.${this.userId}`, data)
        } else {
            await this.initRatings(this.version, this.userId);
        }
    }

    public async updateRating(rating: number) {
        const data = this.storeService.getMetaData(`${this.RATINGS_KEY}.${this.version}.${this.userId}`);
        await this.setRatings({
            ...data,
            rating
        })
    }

    public async sessionUpdate(isReset: boolean = false) {
        if (isReset) {
            if (!this.currentServerName) {
                await this.getCurrentServerName();
            }
            const updateSessionStatus = await this.getUpdatedSessionStatus();
            if (updateSessionStatus) {
                Object.keys(updateSessionStatus).map(key => {
                    updateSessionStatus[key] = false;
                })
            }
            await this.setUpdatedSessionStatus(updateSessionStatus);
            return;
        }
        const localStoreCheckUpdateSession = await this.getUpdatedSessionStatus();
        if (!(localStoreCheckUpdateSession && localStoreCheckUpdateSession[this.version] === true)) {
            const data = this.storeService.getMetaData(`${this.RATINGS_KEY}.${this.version}.${this.userId}`);
            if (!data) {
                await this.initRatings(this.version, this.userId)
                return;
            }
            if (!data.rating) {
                if (this.openBy === 'time') {
                    await this.setRatings({
                        ...data,
                        sessionCount: data.sessionCount + 1,
                        createdTime: new Date().getTime()
                    })
                    return;
                }
                await this.setRatings({
                    ...data,
                    sessionCount: data.sessionCount + 1
                })
            }
        }
    }

    public async isOpenRatingDialog(data: {
        version: string,
        userId: string
    }) {
        this.openBy = '';
        const {userId} = data;
        const localStoreCheckUpdateSession = await this.getUpdatedSessionStatus();
        if (localStoreCheckUpdateSession && localStoreCheckUpdateSession[this.version]) {
            return;
        }
        await this.setUpdatedSessionStatus({
            [this.version]: true
        });
        const versionRatingList = this.storeService.getMetaData(`${this.RATINGS_KEY}.${this.version}`);
        const userRating = (versionRatingList && versionRatingList[userId]) || null;
        if (!versionRatingList ||
            !userRating) {
            await this.initRatings(this.version, userId);
            return this.INITIATING_SESSION === 1;
        }

        if (userRating.rating) {
            return false;
        }
        const sessionCount = userRating.sessionCount;
        if (sessionCount === this.INITIATING_SESSION ||
            (sessionCount >= (this.INITIATING_SESSION + this.REPEAT_PERIOD_BY_SESSION) &&
                ((sessionCount - this.INITIATING_SESSION) % this.REPEAT_PERIOD_BY_SESSION === 0))
        ) {
            await this.setRatings({
                ...userRating,
                createdTime: new Date().getTime()
            })
            return true;
        }

        if (userRating.createdTime < moment(new Date()).subtract(this.REPEAT_PERIOD_BY_DAYS, 'days').format('x')) {
            this.openBy = 'time';
            await this.setRatings({
                ...userRating,
                createdTime: new Date().getTime()
            })
            return true;
        }

        return false;
    }

    public async getCurrentServerName() {
        this.currentServerName = await msDataProvider.getData("backendServerNameCurrentValue");
    }

    private async getUpdatedSessionStatus() {
        return await this.storeService.getMetaData(`${this.IS_UPDATED_SESSION_KEY}.${this.encodeKey(this.currentServerName)}`)
    }

    private async setUpdatedSessionStatus(data: any) {
        return await this.storeService.setMetaData(`${this.IS_UPDATED_SESSION_KEY}.${this.encodeKey(this.currentServerName)}`, data);
    }

    private async getRatingSetting() {
        const config = await msDataProvider.getData("clientSetting");
        if (config.settings && config.settings.rating) {
            this.INITIATING_SESSION = config.settings.rating.initiating_session ? +config.settings.rating.initiating_session : this.INITIATING_SESSION;
            this.REPEAT_PERIOD_BY_SESSION = config.settings.rating.repeat_period_by_session ? +config.settings.rating.repeat_period_by_session : this.REPEAT_PERIOD_BY_SESSION;
            this.REPEAT_PERIOD_BY_DAYS = config.settings.rating.repeat_period_by_day ? +config.settings.rating.repeat_period_by_day : this.REPEAT_PERIOD_BY_DAYS;
        }
    }

    private encodeKey(version) {
        return version.replace(/\./g, '%2E')
    }

    private parseJson(value) {
        try {
            return JSON.parse(value)
        } catch {
            return value;
        }
    }
}

export default new RatingService(StoreServiceInstance);
